import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Camera, ShoppingBag, Sparkles } from "lucide-react";
import { api } from "../lib/api";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const inviteToken = params.get("invite");
    const inviteEmail = params.get("email");
    const roleParam = params.get("role") === "brand" ? "brand" : "creator";

    const [invite, setInvite] = React.useState(null);
    const [form, setForm] = React.useState({
        name: "",
        email: inviteEmail || "",
        password: "",
        role: inviteToken ? "creator" : roleParam,
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        if (inviteToken) {
            api.get(`/invites/${inviteToken}`)
                .then((r) => setInvite(r.data))
                .catch(() => toast.error("Invite link is invalid or already used"));
        }
    }, [inviteToken]);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        const payload = { ...form };
        if (invite && form.role === "creator") payload.invite_token = inviteToken;
        const r = await register(payload);
        setSubmitting(false);
        if (!r.ok) {
            setError(r.error);
            toast.error(r.error);
            return;
        }
        toast.success(invite ? `Welcome — connected with ${invite.brand_name}` : "Account created");
        navigate(form.role === "creator" ? "/creator" : "/brand");
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2 bg-white">
            <div className="flex flex-col justify-center px-6 lg:px-16 py-12 order-2 md:order-1">
                <Link
                    to="/"
                    className="text-sm text-slate-500 hover:text-slate-900 mb-10 inline-flex items-center gap-1 md:hidden"
                >
                    ← Back
                </Link>

                {invite && (
                    <div className="card-ecom p-4 mb-6 surface-warm" data-testid="invite-banner">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-coral flex items-center justify-center text-white font-display font-bold flex-shrink-0">
                                <Sparkles className="w-5 h-5" />
                            </div>
                            <div className="text-sm">
                                <div className="font-display font-semibold text-slate-900">
                                    {invite.brand_name} invited you
                                </div>
                                <div className="text-slate-600">
                                    Sign up below to connect with them on Castline.
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <h1 className="font-display text-4xl font-semibold text-slate-900 mb-2">
                    {invite ? "Accept invite" : "Create your account"}
                </h1>
                <p className="text-sm text-slate-500 mb-8">
                    {invite ? "Set up your creator account." : "Join as a creator or a brand."}
                </p>

                {!invite && (
                    <div className="grid grid-cols-2 gap-3 max-w-md mb-7" data-testid="role-toggle">
                        {[
                            { v: "creator", label: "Creator", desc: "Upload + earn", Icon: Camera },
                            { v: "brand", label: "Brand", desc: "License + run ads", Icon: ShoppingBag },
                        ].map(({ v, label, desc, Icon }) => (
                            <button
                                type="button"
                                key={v}
                                onClick={() => setForm({ ...form, role: v })}
                                className={`p-4 rounded-2xl border text-left transition-all ${
                                    form.role === v
                                        ? "border-coral bg-coral/5 shadow-sm"
                                        : "border-slate-200 hover:border-slate-300"
                                }`}
                                data-testid={`role-${v}`}
                            >
                                <Icon
                                    className={`w-5 h-5 mb-3 ${form.role === v ? "text-coral" : "text-slate-500"}`}
                                />
                                <div className="font-display text-lg font-semibold text-slate-900">
                                    {label}
                                </div>
                                <div className="text-xs text-slate-500">{desc}</div>
                            </button>
                        ))}
                    </div>
                )}

                <form onSubmit={onSubmit} className="space-y-5 max-w-md" data-testid="register-form">
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">
                            {form.role === "brand" ? "Company / brand name" : "Full name"}
                        </Label>
                        <Input
                            value={form.name}
                            onChange={onChange("name")}
                            required
                            className="rounded-xl border-slate-200 h-12 focus-visible:ring-coral focus-visible:ring-2"
                            data-testid="register-name"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Email</Label>
                        <Input
                            type="email"
                            value={form.email}
                            onChange={onChange("email")}
                            required
                            disabled={!!invite}
                            className="rounded-xl border-slate-200 h-12 focus-visible:ring-coral focus-visible:ring-2 disabled:bg-slate-50"
                            data-testid="register-email"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Password</Label>
                        <Input
                            type="password"
                            value={form.password}
                            onChange={onChange("password")}
                            required
                            minLength={6}
                            className="rounded-xl border-slate-200 h-12 focus-visible:ring-coral focus-visible:ring-2"
                            data-testid="register-password"
                        />
                    </div>
                    {error && (
                        <div className="text-sm text-red-500" data-testid="register-error">
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-pill-primary w-full"
                        data-testid="register-submit"
                    >
                        {submitting ? "Creating…" : invite ? "Accept & create account →" : "Create account →"}
                    </button>
                    <div className="text-sm text-slate-500">
                        Already have one?{" "}
                        <Link to="/login" className="text-coral font-semibold hover:underline">
                            Log in
                        </Link>
                    </div>
                </form>
            </div>
            <div className="hidden md:flex relative bg-slate-900 overflow-hidden flex-col justify-end p-12 order-1 md:order-2">
                <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-lime/20 rounded-full blur-3xl" />
                <div className="absolute -top-20 -right-20 w-96 h-96 bg-coral/30 rounded-full blur-3xl" />
                <Link to="/" className="absolute top-12 right-12 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center text-white font-display font-bold text-lg">
                        c
                    </div>
                    <span className="font-display text-xl font-semibold text-white">castline</span>
                </Link>
                <div className="relative">
                    <h2 className="font-display text-4xl md:text-5xl text-white font-semibold leading-tight tracking-tight max-w-md">
                        Make it once.{" "}
                        <span className="text-lime">Get paid every time it performs.</span>
                    </h2>
                    <p className="text-slate-400 mt-4 max-w-sm">
                        Castline turns creator content into reusable ad inventory. Brands run it
                        everywhere — creators earn an ongoing share of the upside.
                    </p>
                </div>
            </div>
        </div>
    );
}
